import React, { useState, useRef, useEffect } from 'react';
import { Trash2, Copy, RotateCcw, Code, Plus, ArrowRight, Venn } from 'lucide-react';
import "../../assets/css/CreateYourOwn/CommonCreate.css"
import "../../assets/css/CreateYourOwn/SetBuilder.css"
import { useDarkMode } from 'context/DarkModeProvider';
import "../../assets/css/colors.css"
import { useLanguage } from 'context/LanguageProvider';
import CodeHighlighter from 'components/CodeHighlighter';


const SetBuilder = () => {
    const [sets, setSets] = useState([
        { id: 1, name: 'Set A', elements: [], color: '#667eea', x: 150, y: 150 }
    ]);
    const [nextSetId, setNextSetId] = useState(2);
    const [selectedSet, setSelectedSet] = useState(1);
    const [newElement, setNewElement] = useState('');
    const [newSetName, setNewSetName] = useState('');
    const [editingSetId, setEditingSetId] = useState(null);
    const [animatingOperation, setAnimatingOperation] = useState(null);
    const [codeLanguage, setCodeLanguage] = useState('javascript');
    const [showInfo, setShowInfo] = useState(false);
    const [operationResult, setOperationResult] = useState(null);
    const [persistentResult, setPersistentResult] = useState(null);
    const containerRef = useRef(null);
    const [counter, setCounter] = useState(1);
    const [copied, setCopied] = useState(false);
    const [actionLog, setActionLog] = useState([]);
    const { darkMode } = useDarkMode();
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    const colors = ['#667eea'];

    const animateOperation = (operation, setId = null, result = null) => {
        setAnimatingOperation({ operation, setId });
        if (result) {
            setPersistentResult(result);
            setTimeout(() => setOperationResult(null), 800);
        }
        setTimeout(() => setAnimatingOperation(null), 600);
    };

    // Add new set
    const addSet = (name = null) => {
        const getSpacing = () => {
            if (window.innerWidth <= 480) return { horizontal: 145, vertical: 160 };
            else if (window.innerWidth <= 768) return { horizontal: 170, vertical: 190 };
            return { horizontal: 250, vertical: 200 };
        };
        const spacing = getSpacing();
        const setName = name || newSetName || `Set ${String.fromCharCode(64 + nextSetId)}`;
        const newSet = {
            id: nextSetId,
            name: setName,
            elements: [],
            color: colors[nextSetId % colors.length],
            x: 200 + (sets.length % 4) * spacing.horizontal,
            y: 150 + Math.floor(sets.length / 4) * spacing.vertical
        };
        setSets(prev => [...prev, newSet]);
        setNextSetId(prev => prev + 1);
        setSelectedSet(newSet.id);
        animateOperation('addSet', newSet.id);
        setNewSetName('');
        setActionLog(prev => [...prev, { type: 'addSet', setName }]);
    };

    const renameSet = (setId, newName) => {
        if (newName.trim()) {
            setSets(prev => prev.map(set =>
                set.id === setId ? { ...set, name: newName.trim() } : set
            ));
        }
        setEditingSetId(null);
    };

    // Add element to selected set
    const addElement = (element) => {
        if (!selectedSet) return;
        if (!element.trim()) {
            element = "item" + counter;
            setCounter(counter + 1);
        }
        const targetSet = sets.find(s => s.id === selectedSet);
        if (!targetSet || targetSet.elements.includes(element.trim())) {
            alert("Can not add the same element in the set.");
            animateOperation('duplicate', selectedSet);
            return;
        }
        setSets(prev => prev.map(set =>
            set.id === selectedSet
                ? { ...set, elements: [...set.elements, element.trim()] }
                : set
        ));
        animateOperation('addElement', selectedSet);
        setNewElement('');
        const setName = targetSet.name;
        setActionLog(prev => [...prev, { type: 'addElement', setName, value: element.trim() }]);
    };

    // Remove element from set
    const removeElement = (setId, element) => {
        const targetSet = sets.find(s => s.id === setId);
        setSets(prev => prev.map(set =>
            set.id === setId
                ? { ...set, elements: set.elements.filter(e => e !== element) }
                : set
        ));
        animateOperation('removeElement', setId);
        setActionLog(prev => [...prev, { type: 'removeElement', setName: targetSet?.name, value: element }]);
    };

    // Delete set
    const deleteSet = (setId) => {
        if (sets.length <= 1) return;
        const targetSet = sets.find(s => s.id === setId);
        setSets(prev => prev.filter(s => s.id !== setId));
        if (selectedSet === setId) {
            setSelectedSet(sets.find(s => s.id !== setId)?.id || sets[0].id);
        }
        animateOperation('deleteSet', setId);
        setActionLog(prev => [...prev, { type: 'deleteSet', setName: targetSet?.name }]);
    };

    // Clear set elements
    const clearSet = (setId) => {
        const targetSet = sets.find(s => s.id === setId);
        setSets(prev => prev.map(set =>
            set.id === setId ? { ...set, elements: [] } : set
        ));
        animateOperation('clear', setId);
        setActionLog(prev => [...prev, { type: 'clearSet', setName: targetSet?.name }]);
    };

    // Set operations
    const performUnion = () => {
        if (sets.length < 2) return;
        const [setA, setB] = sets.slice(0, 2);
        const unionElements = [...new Set([...setA.elements, ...setB.elements])];
        const result = { operation: 'union', elements: unionElements, sets: [setA.name, setB.name] };
        animateOperation('union', null, result);
        setActionLog(prev => [...prev, { type: 'union', setA: setA.name, setB: setB.name }]);
    };

    const performIntersection = () => {
        if (sets.length < 2) return;
        const [setA, setB] = sets.slice(0, 2);
        const intersectionElements = setA.elements.filter(e => setB.elements.includes(e));
        const result = { operation: 'intersection', elements: intersectionElements, sets: [setA.name, setB.name] };
        animateOperation('intersection', null, result);
        setActionLog(prev => [...prev, { type: 'intersection', setA: setA.name, setB: setB.name }]);
    };

    const performDifference = () => {
        if (sets.length < 2) return;
        const [setA, setB] = sets.slice(0, 2);
        const differenceElements = setA.elements.filter(e => !setB.elements.includes(e));
        const result = { operation: 'difference', elements: differenceElements, sets: [setA.name, setB.name] };
        animateOperation('difference', null, result);
        setActionLog(prev => [...prev, { type: 'difference', setA: setA.name, setB: setB.name }]);
    };

    const getVarName = (name, lang) =>
        lang === 'python' || lang === 'cpp'
            ? name.replace(/\s+/g, '_').toLowerCase()
            : name.replace(/\s+/g, '').toLowerCase();

    const generateCode = () => {

        const renderActions = (lang) => {
            return actionLog.map(action => {
                const sn = action.setName ? getVarName(action.setName, lang) : '';
                const saV = action.setA ? getVarName(action.setA, lang) : '';
                const sbV = action.setB ? getVarName(action.setB, lang) : '';

                if (action.type === 'addSet') {
                    const v = getVarName(action.setName, lang);
                    if (lang === 'javascript') return `const ${v} = new Set();`;
                    if (lang === 'python') return `${v} = set()`;
                    if (lang === 'java') return `        HashSet<String> ${v} = new HashSet<>();`;
                    if (lang === 'cpp') return `    set<string> ${v};`;
                }
                if (action.type === 'addElement') {
                    if (lang === 'javascript') return `${sn}.add("${action.value}");`;
                    if (lang === 'python') return `${sn}.add("${action.value}")`;
                    if (lang === 'java') return `        ${sn}.add("${action.value}");`;
                    if (lang === 'cpp') return `    ${sn}.insert("${action.value}");`;
                }
                if (action.type === 'removeElement') {
                    if (lang === 'javascript') return `${sn}.delete("${action.value}");`;
                    if (lang === 'python') return `${sn}.discard("${action.value}")`;
                    if (lang === 'java') return `        ${sn}.remove("${action.value}");`;
                    if (lang === 'cpp') return `    ${sn}.erase("${action.value}");`;
                }
                if (action.type === 'clearSet') {
                    if (lang === 'javascript') return `${sn}.clear();`;
                    if (lang === 'python') return `${sn}.clear()`;
                    if (lang === 'java') return `        ${sn}.clear();`;
                    if (lang === 'cpp') return `    ${sn}.clear();`;
                }
                if (action.type === 'deleteSet') {
                    if (lang === 'javascript') return `// Set "${action.setName}" was deleted`;
                    if (lang === 'python') return `# Set "${action.setName}" was deleted`;
                    if (lang === 'java') return `        // Set "${action.setName}" was deleted`;
                    if (lang === 'cpp') return `    // Set "${action.setName}" was deleted`;
                }
                if (action.type === 'union') {
                    if (lang === 'javascript') return `const union_${saV}_${sbV} = SetOperations.union(${saV}, ${sbV});`;
                    if (lang === 'python') return `union_${saV}_${sbV} = ${saV} | ${sbV}`;
                    if (lang === 'java') return `        HashSet<String> union_${saV}_${sbV} = union(${saV}, ${sbV});`;
                    if (lang === 'cpp') return `    set<string> union_${saV}_${sbV} = setUnion(${saV}, ${sbV});`;
                }

                if (action.type === 'intersection') {
                    if (lang === 'javascript') return `const inter_${saV}_${sbV} = SetOperations.intersection(${saV}, ${sbV});`;
                    if (lang === 'python') return `inter_${saV}_${sbV} = ${saV} & ${sbV}`;
                    if (lang === 'java') return `        HashSet<String> inter_${saV}_${sbV} = intersection(${saV}, ${sbV});`;
                    if (lang === 'cpp') return `    set<string> inter_${saV}_${sbV} = setIntersection(${saV}, ${sbV});`;
                }
                if (action.type === 'difference') {
                    if (lang === 'javascript') return `const diff_${saV}_${sbV} = SetOperations.difference(${saV}, ${sbV});`;
                    if (lang === 'python') return `diff_${saV}_${sbV} = ${saV} - ${sbV}`;
                    if (lang === 'java') return `        HashSet<String> diff_${saV}_${sbV} = difference(${saV}, ${sbV});`;
                    if (lang === 'cpp') return `    set<string> diff_${saV}_${sbV} = setDifference(${saV}, ${sbV});`;
                }
                return '';
            }).filter(Boolean).join('\n');
        };

        const codes = {
            javascript: `// Set Implementation in JavaScript

class SetOperations {
    static union(setA, setB) {
        return new Set([...setA, ...setB]);
    }

    static intersection(setA, setB) {
        return new Set([...setA].filter(x => setB.has(x)));
    }

    static difference(setA, setB) {
        return new Set([...setA].filter(x => !setB.has(x)));
    }

    static isSubset(setA, setB) {
        return [...setA].every(x => setB.has(x));
    }

    static isSuperset(setA, setB) {
        return [...setB].every(x => setA.has(x));
    }
}

// Main
const seta = new Set();
${renderActions('javascript')}`,

            python: `# Set Implementation in Python

class SetOperations:
    @staticmethod
    def union(set_a, set_b):
        return set_a | set_b

    @staticmethod
    def intersection(set_a, set_b):
        return set_a & set_b

    @staticmethod
    def difference(set_a, set_b):
        return set_a - set_b

    @staticmethod
    def symmetric_difference(set_a, set_b):
        return set_a ^ set_b

    @staticmethod
    def is_subset(set_a, set_b):
        return set_a <= set_b

    @staticmethod
    def is_superset(set_a, set_b):
        return set_a >= set_b

# Main
if __name__ == "__main__":
    set_a = set()
${renderActions('python').split('\n').map(l => '    ' + l).join('\n')}`,

            java: `// Set Implementation in Java
import java.util.HashSet;

public class Main {

    public static HashSet<String> union(HashSet<String> setA, HashSet<String> setB) {
        HashSet<String> result = new HashSet<>(setA);
        result.addAll(setB);
        return result;
    }

    public static HashSet<String> intersection(HashSet<String> setA, HashSet<String> setB) {
        HashSet<String> result = new HashSet<>(setA);
        result.retainAll(setB);
        return result;
    }

    public static HashSet<String> difference(HashSet<String> setA, HashSet<String> setB) {
        HashSet<String> result = new HashSet<>(setA);
        result.removeAll(setB);
        return result;
    }

    public static boolean isSubset(HashSet<String> setA, HashSet<String> setB) {
        return setB.containsAll(setA);
    }

    public static void main(String[] args) {
        HashSet<String> seta = new HashSet<>();
${renderActions('java')}
    }
}`,

            cpp: `// Set Implementation in C++
#include <iostream>
#include <set>
#include <string>
#include <algorithm>
#include <iterator>
using namespace std;

set<string> setUnion(const set<string>& setA, const set<string>& setB) {
    set<string> result;
    set_union(setA.begin(), setA.end(), setB.begin(), setB.end(),
              inserter(result, result.begin()));
    return result;
}

set<string> setIntersection(const set<string>& setA, const set<string>& setB) {
    set<string> result;
    set_intersection(setA.begin(), setA.end(), setB.begin(), setB.end(),
                     inserter(result, result.begin()));
    return result;
}

set<string> setDifference(const set<string>& setA, const set<string>& setB) {
    set<string> result;
    set_difference(setA.begin(), setA.end(), setB.begin(), setB.end(),
                   inserter(result, result.begin()));
    return result;
}

bool isSubset(const set<string>& setA, const set<string>& setB) {
    return includes(setB.begin(), setB.end(), setA.begin(), setA.end());
}

int main() {
    set<string> seta;
${renderActions('cpp')}
    return 0;
}`
        };

        return codes[codeLanguage];
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.ctrlKey || e.metaKey) {
                switch (e.key) {
                    case 'Enter':
                        e.preventDefault();
                        if (newElement.trim()) addElement(newElement);
                        break;
                    case 'n':
                        e.preventDefault();
                        addSet();
                        break;
                }
            }
        };
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [newElement, sets]);

    const buttonStyle = {
        background: darkMode ? "#1e293b" : "var(--customBuildButton)",
        fontFamily: "Poppins, sans-serif",
        color: "#7a8effff",
        border: "2px solid #7a8effff",
        minWidth: 0,
        overflow: 'hidden'
    };

    return (
        <div
            className='commonContainerCreate'
            style={{ background: darkMode ? "#0F172A" : "var(--lightModeBack)" }}
        >
            <div
                className='commonVisual'
                style={{
                    background: darkMode ? "#1e293b" : "var(--customBuildBack)",
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderColor: darkMode ? 'rgba(122, 142, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)',
                }}
            >
                <h1 className='commonCreateTitle'>
                    {selLanguage ? "Δημιουργία συνόλου" : "Set builder"}
                </h1>

                {/* Input Controls */}
                <div className="input-info-container" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                    <div>
                        <input
                            type="text"
                            value={newElement}
                            onChange={(e) => setNewElement(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && addElement(newElement)}
                            placeholder={selLanguage ? "Προσθήκη στοιχείου στο σύνολο" : "Add element to selected set..."}
                            style={{
                                width: '100%',
                                padding: '16px 20px',
                                border: '2px solid #e5e7eb',
                                borderRadius: '16px',
                                fontSize: '1rem',
                                background: 'white',
                                transition: 'all 0.2s ease',
                                fontFamily: 'inherit',
                            }}
                            className="custom-input"
                        />
                        <style>{`.custom-input::placeholder { color: #363a42ff; opacity: 1; }`}</style>
                    </div>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                        <button
                            className='commonButtonCreate'
                            style={{
                                background: darkMode ? "#1e293b" : "var(--customBuildButton)",
                                fontFamily: "Poppins, sans-serif",
                                color: "#7a8effff",
                                border: "2px solid #7a8effff",
                            }}
                            onMouseEnter={() => setShowInfo(true)}
                            onMouseLeave={() => setShowInfo(false)}
                        >
                            💡
                        </button>
                        {showInfo && (
                            <div style={{
                                position: 'absolute', top: '100%', left: '8%', transform: 'translateX(-50%)',
                                marginTop: '10px', padding: '20px',
                                background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.98), rgba(51, 65, 85, 0.98))',
                                backdropFilter: 'blur(20px)', borderRadius: '16px',
                                border: '1px solid rgba(122, 142, 255, 0.3)',
                                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)', zIndex: 1000, minWidth: '280px'
                            }}>
                                <div style={{ position: 'absolute', top: '-8px', left: '50%', transform: 'translateX(-50%)', width: '0', height: '0', borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderBottom: '8px solid rgba(30, 41, 59, 0.98)' }} />
                                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#ffffff', marginBottom: '15px', textAlign: 'center' }}>
                                    {selLanguage ? "Οδηγίες Χρήσης" : "Instructions"}
                                </h3>
                                <div style={{ fontSize: '0.85rem', color: '#cbd5e1', lineHeight: '1.6' }}>
                                    <div style={{ marginBottom: '8px' }}><strong style={{ color: '#7a8eff' }}>{selLanguage ? "Κλικ στο σετ:" : "Click set:"}</strong> {selLanguage ? "Επιλέξτε το ενεργό σετ" : "Select active set"}</div>
                                    <div style={{ marginBottom: '8px' }}><strong style={{ color: '#7a8eff' }}>{selLanguage ? "Προσθήκη στοιχείων:" : "Add elements:"}</strong> {selLanguage ? "Δεν επιτρέπονται διπλότυπα" : "No duplicates allowed"}</div>
                                    <div style={{ marginBottom: '8px' }}><strong style={{ color: '#7a8eff' }}>{selLanguage ? "Ένωση:" : "Union:"}</strong> {selLanguage ? "Όλα τα μοναδικά στοιχεία" : "All unique elements from both sets"}</div>
                                    <div style={{ marginBottom: '8px' }}><strong style={{ color: '#7a8eff' }}>{selLanguage ? "Τομή:" : "Intersection:"}</strong> {selLanguage ? "Μόνο τα κοινά στοιχεία" : "Common elements only"}</div>
                                    <div style={{ marginBottom: '8px' }}><strong style={{ color: '#7a8eff' }}>{selLanguage ? "Διαφορά:" : "Difference:"}</strong> {selLanguage ? "Στοιχεία μόνο στο πρώτο σύνολο" : "Elements in first set only"}</div>
                                    <div><strong style={{ color: '#7a8eff' }}>Ctrl+Enter:</strong> {selLanguage ? "Προσθήκη στοιχείου" : "Add element"}, <strong style={{ color: '#7a8eff' }}>Ctrl+N:</strong> {selLanguage ? "Νέο σετ" : "New set"}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Operation Buttons */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '15px', marginBottom: '20px', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                    <button onClick={() => addElement(newElement)} className='commonButtonCreate' style={buttonStyle}>
                        <Plus size={16} style={{ marginRight: '8px' }} />
                        {selLanguage ? "Προσθήκη στοιχείου" : "Add Element"}
                    </button>
                    <button onClick={() => addSet()} className='commonButtonCreate' style={buttonStyle}>
                        <Plus size={16} style={{ marginRight: '8px' }} />
                        {selLanguage ? "Νέο σετ" : "New Set"}
                    </button>
                    <button onClick={() => selectedSet && clearSet(selectedSet)} disabled={!selectedSet} className='commonButtonCreate' style={buttonStyle}>
                        <RotateCcw size={16} style={{ marginRight: '8px' }} />
                        {selLanguage ? "Καθαρισμός συνόλου" : "Clear Set"}
                    </button>
                    <button className='commonButtonCreate' onClick={() => selectedSet && deleteSet(selectedSet)} disabled={!selectedSet || sets.length <= 1} style={buttonStyle}>
                        <Trash2 size={16} style={{ marginRight: '8px' }} />
                        {selLanguage ? "Διαγραφή συνόλου" : "Delete Set"}
                    </button>
                </div>

                {/* Persistent Result Display */}
                {persistentResult && (
                    <div style={{ background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))', border: '2px solid rgba(102, 126, 234, 0.3)', borderRadius: '16px', padding: '20px', marginBottom: '20px', position: 'relative' }}>
                        <button onClick={() => setPersistentResult(null)} style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(239, 68, 68, 0.8)', border: 'none', borderRadius: '50%', width: '24px', height: '24px', color: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Close result">×</button>
                        <div style={{ fontSize: '1.2rem', fontWeight: '700', color: 'white', marginBottom: '12px', textTransform: 'capitalize' }}>
                            {persistentResult.operation === 'union' && '∪ Union'}
                            {persistentResult.operation === 'intersection' && '∩ Intersection'}
                            {persistentResult.operation === 'difference' && '− Difference'}
                            {' '}Result: {persistentResult.sets.join(' and ')}
                        </div>
                        <div style={{ background: 'rgba(255,255,255,0.15)', padding: '12px', borderRadius: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px', minHeight: '40px', alignItems: 'center' }}>
                            {persistentResult.elements.length === 0 ? (
                                <span style={{ color: 'white', fontStyle: 'italic', fontSize: '1rem' }}>∅ (empty set)</span>
                            ) : (
                                persistentResult.elements.map((elem, i) => (
                                    <span key={i} style={{ background: 'rgba(255,255,255,0.25)', color: 'white', padding: '6px 12px', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '500' }}>{elem}</span>
                                ))
                            )}
                        </div>
                    </div>
                )}

                {/* Set Operation Buttons */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '30px' }}>
                    <button className='commonButtonCreate operationButton' onClick={performUnion} disabled={sets.length < 2} style={buttonStyle}>
                        ∪{selLanguage ? " Ένωση" : " Union"}
                    </button>
                    <button className='commonButtonCreate operationButton' onClick={performIntersection} disabled={sets.length < 2} style={buttonStyle}>
                        ∩ {selLanguage ? " Τομή" : " Intersection"}
                    </button>
                    <button className='commonButtonCreate operationButton' onClick={performDifference} disabled={sets.length < 2} style={buttonStyle}>
                        - {selLanguage ? " Διαφορά" : " Difference"}
                    </button>
                </div>

                {/* Sets Visualization */}
                <div ref={containerRef} className='setVisualCreate' style={{ border: "1px solid #1f1f1fff" }}>
                    {sets.map((set) => (
                        <div
                            key={set.id}
                            onClick={() => setSelectedSet(set.id)}
                            onDoubleClick={() => setEditingSetId(set.id)}
                            style={{
                                position: 'absolute', left: set.x, top: set.y,
                                minWidth: '200px', maxWidth: '250px',
                                backgroundColor: selectedSet === set.id ? set.color : '#ffffff',
                                border: `3px solid ${selectedSet === set.id ? set.color : '#e2e8f0'}`,
                                borderRadius: '16px', padding: '20px', cursor: 'pointer',
                                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                transform: animatingOperation?.setId === set.id ? 'scale(1.05)' : 'scale(1)',
                                boxShadow: selectedSet === set.id ? `0 10px 25px ${set.color}40` : '0 5px 15px rgba(0,0,0,0.1)',
                                zIndex: selectedSet === set.id ? 20 : 10
                            }}
                        >
                            <div style={{ fontSize: '1.2rem', fontWeight: '700', color: selectedSet === set.id ? 'white' : set.color, marginBottom: '15px', textAlign: 'center', borderBottom: `2px solid ${selectedSet === set.id ? 'rgba(255,255,255,0.3)' : set.color + '30'}`, paddingBottom: '10px' }}>
                                {editingSetId === set.id ? (
                                    <input type="text" defaultValue={set.name} style={{ background: 'transparent', border: 'none', color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', textAlign: 'center', outline: 'none', width: '100%' }}
                                        autoFocus
                                        onBlur={(e) => renameSet(set.id, e.target.value)}
                                        onKeyPress={(e) => { if (e.key === 'Enter') renameSet(set.id, e.target.value); }}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                ) : set.name}
                                <div style={{ fontSize: '0.75rem', opacity: 0.8, fontWeight: '500' }}>
                                    {set.elements.length} {selLanguage ? "Στοιχεία" : "elements"}
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', minHeight: '60px' }}>
                                {set.elements.length === 0 ? (
                                    <div style={{ color: selectedSet === set.id ? 'rgba(255,255,255,0.7)' : '#9ca3af', fontStyle: 'italic', fontSize: '0.9rem', width: '100%', textAlign: 'center' }}>
                                        {selLanguage ? " Άδειο σύνολο" : " Empty set"} ∅
                                    </div>
                                ) : (
                                    set.elements.map((element, index) => (
                                        <div key={index}
                                            onClick={(e) => { e.stopPropagation(); removeElement(set.id, element); }}
                                            style={{ background: selectedSet === set.id ? 'rgba(255,255,255,0.2)' : set.color + '15', color: selectedSet === set.id ? 'white' : set.color, padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: '500', cursor: 'pointer', border: `1px solid ${selectedSet === set.id ? 'rgba(255,255,255,0.3)' : set.color + '30'}`, transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', gap: '4px' }}
                                            title="Click to remove"
                                        >
                                            {element}
                                            <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>×</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    ))}

                    {/* Operation Result */}
                    {operationResult && (
                        <div className='operationSet'>
                            <div style={{ fontSize: '1rem', marginBottom: '8px', textTransform: 'capitalize' }}>{operationResult.operation} Result:</div>
                            <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '8px' }}>{operationResult.sets.join(' ∩ ')}</div>
                            <div style={{ fontSize: '0.8rem', background: 'rgba(255,255,255,0.2)', padding: '8px', borderRadius: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                {operationResult.elements.length === 0 ? (
                                    <span style={{ fontStyle: 'italic' }}>∅ (empty set)</span>
                                ) : (
                                    operationResult.elements.map((elem, i) => (
                                        <span key={i} style={{ background: 'rgba(255,255,255,0.3)', padding: '2px 6px', borderRadius: '4px' }}>{elem}</span>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {/* Operation Indicator */}
                    {animatingOperation && (
                        <div className='animationSetCreate'>
                            {animatingOperation.operation === 'addSet' && 'ADDING SET'}
                            {animatingOperation.operation === 'addElement' && 'ADDING ELEMENT'}
                            {animatingOperation.operation === 'removeElement' && 'REMOVING ELEMENT'}
                            {animatingOperation.operation === 'deleteSet' && 'DELETING SET'}
                            {animatingOperation.operation === 'clear' && 'CLEARING SET'}
                            {animatingOperation.operation === 'duplicate' && 'DUPLICATE ELEMENT'}
                            {animatingOperation.operation === 'union' && 'UNION OPERATION'}
                            {animatingOperation.operation === 'intersection' && 'INTERSECTION'}
                            {animatingOperation.operation === 'difference' && 'DIFFERENCE'}
                        </div>
                    )}
                </div>

                {/* Language Selector */}
                <div style={{ marginBottom: '20px' }}>
                    <label className='codeLabelCreate' style={{ color: darkMode ? "#fff" : "#0b0b0bff" }}>
                        {selLanguage ? "Γλώσσα Προγραμματισμού" : "Programming Language"}
                    </label>
                    <select className='codeSelectorCreate' value={codeLanguage} onChange={(e) => setCodeLanguage(e.target.value)}>
                        <option value="javascript">JavaScript</option>
                        <option value="python">Python</option>
                        <option value="java">Java</option>
                        <option value="cpp">C++</option>
                    </select>
                </div>

                {/* Code Display */}
                <div className='codeBlockCreate'>
                    <CodeHighlighter code={generateCode()} language={codeLanguage} />
                </div>

                {/* Copy Button */}
                <button
                    className='commonButtonCreate'
                    onClick={() => {
                        navigator.clipboard.writeText(generateCode());
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                    }}
                    style={{
                        width: '100%', marginTop: '20px',
                        background: copied ? 'linear-gradient(135deg, #059669 0%, #047857 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        transform: copied ? 'translateY(-4px)' : 'translateY(0)',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseOver={(e) => !copied && (e.target.style.transform = 'translateY(-2px)')}
                    onMouseOut={(e) => !copied && (e.target.style.transform = 'translateY(0)')}
                >
                    <Copy size={16} style={{ marginRight: '8px' }} />
                    {copied ? (selLanguage ? ' Αντιγράφηκε!' : ' Copied') : (selLanguage ? ' Αντιγραφή' : ' Copy')}
                </button>
            </div>
        </div>
    );
};

export default SetBuilder;